// The plugin detail page: the README (rendered safely through the sanitized
// pipeline) plus the FULL permission disclosure (Requires / May request later +
// the access badge) and the lifecycle actions. The page is itself the
// host-rendered consent surface (decision D3): clicking Install after reading
// the disclosure is the consent; the marketplace never grants silently. An
// update that adds a new mandatory permission surfaces a re-consent banner and
// the plugin stays disabled until the user approves (spec 9.4).

import { useState, type ReactElement } from 'react';
import {
  ArrowLeft,
  BadgeCheck,
  ChevronDown,
  Clipboard,
  FilePen,
  FileText,
  Globe,
  Shield,
  Terminal,
  type LucideIcon,
} from 'lucide-react';
import type { PermissionRequest } from '@slate/plugin-api';
import { CAPABILITY_LABELS } from '../plugins/accessBadge';
import { commandSlash } from '../plugins/pluginCommandSlash';
import { useDocStore } from '../store/docStore';
import { Modal, type ModalAction } from '../components/Modal';
import { computeDisclosure, type DisclosureLine } from './permissionDisclosure';

/** A glyph per capability, so a permission reads at a glance before its reason. */
const CAPABILITY_ICONS: Record<string, LucideIcon> = {
  'fs.read': FileText,
  'fs.write': FilePen,
  pty: Terminal,
  net: Globe,
  clipboard: Clipboard,
};
import type { PluginDetail as Detail } from './source';
import type { InstallFlow } from './installFlow';
import { PluginReadme } from './PluginReadme';

export interface PluginDetailProps {
  detail: Detail;
  installed: boolean;
  enabled: boolean;
  /** True when the source offers a newer version than the installed one. */
  updateAvailable?: boolean;
  flow: InstallFlow;
  /** Invoked after any state-changing action so the shell can refresh. */
  onChanged(): void;
  /** Invoked by the inline Back button to return to the list. When omitted the
   *  button is not rendered (e.g. when the detail is shown without a list). */
  onBack?(): void;
  onOpenLink?(href: string): void;
}

/** One permission as an accordion row: icon + name + capability id in the header,
 *  and the author's reason (plus scope/domains) revealed when expanded. Mandatory
 *  permissions open by default for transparency; optional ones stay collapsed. */
function PermissionRow({ line, defaultOpen }: { line: DisclosureLine; defaultOpen: boolean }): ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = CAPABILITY_ICONS[line.capability] ?? Shield;
  return (
    <li className="mp-perm" data-open={open}>
      <button
        type="button"
        className="mp-perm__head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon className="mp-perm__icon" size={16} aria-hidden />
        <span className="mp-perm__name">{CAPABILITY_LABELS[line.capability]}</span>
        <code className="mp-perm__id">{line.capability}</code>
        <ChevronDown className="mp-perm__chevron" size={14} aria-hidden />
      </button>
      {open && (
        <div className="mp-perm__body">
          <p className="mp-perm__reason">{line.reason}</p>
          {line.scope && (
            <p className="mp-perm__meta">Scope: <strong>{line.scope}</strong></p>
          )}
          {line.domains && line.domains.length > 0 && (
            <p className="mp-perm__meta">Domains: <strong>{line.domains.join(', ')}</strong></p>
          )}
        </div>
      )}
    </li>
  );
}

function DisclosureGroup({
  label,
  lines,
  defaultOpen,
}: {
  label: string;
  lines: DisclosureLine[];
  defaultOpen: boolean;
}): ReactElement {
  return (
    <section className="mp-disclosure__group" role="group" aria-label={label}>
      <h4 className="mp-disclosure__group-title">{label}</h4>
      {lines.length === 0 ? (
        <p className="mp-disclosure__none">None.</p>
      ) : (
        <ul className="mp-disclosure__list" role="list">
          {lines.map((line) => (
            <PermissionRow key={line.capability} line={line} defaultOpen={defaultOpen} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function PluginDetail({
  detail,
  installed,
  enabled,
  updateAvailable = false,
  flow,
  onChanged,
  onBack,
  onOpenLink,
}: PluginDetailProps): ReactElement {
  const disclosure = computeDisclosure(detail.manifest);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reconsent, setReconsent] = useState<PermissionRequest[] | null>(null);
  // Install and Uninstall are gated behind an explicit in-app confirmation (Modal),
  // so neither runs on a single accidental click. Enable/Disable are not gated.
  const [confirm, setConfirm] = useState<'install' | 'uninstall' | null>(null);
  const openPaletteWithSeed = useDocStore((s) => s.openPaletteWithSeed);

  // Clicking a command opens the palette pre-filled with that command's slash, so
  // the disclosure page doubles as a launcher. The command then runs from the
  // palette exactly as if the user had typed it.
  const runInPalette = (commandId: string): void => {
    openPaletteWithSeed(commandSlash(detail.manifest, commandId));
  };

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="mp-detail" aria-label={`${detail.name} details`}>
      <header className="mp-detail__head">
        {onBack && (
          <button
            type="button"
            className="mp-detail__back"
            onClick={onBack}
            aria-label="Back"
            title="Back to list"
          >
            <ArrowLeft size={16} aria-hidden />
          </button>
        )}
        <h2 className="mp-detail__name">{detail.name}</h2>
        <span className="mp-detail__publisher">{detail.publisher}</span>
        <span className="mp-detail__version">v{detail.version}</span>
        {detail.firstParty && (
          <span className="mp-detail__official" title="Official" aria-label="Official">
            <BadgeCheck size={16} aria-hidden />
          </span>
        )}
      </header>

      <section
        className="mp-disclosure"
        aria-label="Permissions"
        data-badge-level={disclosure.badge.level}
      >
        <span
          className="mp-disclosure__badge"
          data-level={disclosure.badge.level}
          aria-label={`Access level: ${disclosure.badge.label}. ${disclosure.badge.detail}.`}
        >
          <span className="mp-disclosure__badge-label">{disclosure.badge.label}</span>
          <span className="mp-disclosure__badge-detail" aria-hidden>
            {disclosure.badge.detail}
          </span>
        </span>
        <DisclosureGroup label="Requires" lines={disclosure.requires} defaultOpen />
        <DisclosureGroup label="May request later" lines={disclosure.optional} defaultOpen={false} />
      </section>

      <div className="mp-detail__actions">
        {!installed && (
          <button
            type="button"
            className="mp-btn mp-btn--primary"
            disabled={busy}
            onClick={() => setConfirm('install')}
          >
            Install
          </button>
        )}
        {installed && (
          <>
            <button
              type="button"
              className="mp-btn"
              disabled={busy}
              onClick={() => void run(() => flow.setEnabled(detail.id, !enabled))}
            >
              {enabled ? 'Disable' : 'Enable'}
            </button>
            {updateAvailable && (
              <button
                type="button"
                className="mp-btn"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    const res = await flow.update(detail.id);
                    setReconsent(res.newMandatoryPermissions.length > 0 ? res.newMandatoryPermissions : null);
                  })
                }
              >
                Update
              </button>
            )}
            <button
              type="button"
              className="mp-btn mp-btn--danger"
              disabled={busy}
              onClick={() => setConfirm('uninstall')}
            >
              Uninstall
            </button>
          </>
        )}
      </div>

      {confirm === 'install' && (
        <Modal
          open
          onClose={() => setConfirm(null)}
          title="Install plugin"
          actions={
            [
              { id: 'cancel', label: 'Cancel', onSelect: () => setConfirm(null), accelerator: 'c', isCancel: true },
              {
                id: 'confirm',
                label: 'Confirm',
                accelerator: '',
                variant: 'primary',
                isDefault: true,
                onSelect: () => {
                  setConfirm(null);
                  void run(() => flow.install(detail.id));
                },
              },
            ] satisfies ModalAction[]
          }
        >
          <p className="mp-modal__text">
            Install <strong>{detail.name}</strong>? Access level:{' '}
            <strong>{disclosure.badge.label}</strong> ({disclosure.badge.detail}).
          </p>
        </Modal>
      )}

      {confirm === 'uninstall' && (
        <Modal
          open
          onClose={() => setConfirm(null)}
          title="Uninstall plugin"
          actions={
            [
              { id: 'cancel', label: 'Cancel', onSelect: () => setConfirm(null), accelerator: 'c', isCancel: true },
              {
                id: 'confirm',
                label: 'Confirm',
                accelerator: '',
                variant: 'danger',
                isDefault: true,
                onSelect: () => {
                  setConfirm(null);
                  void run(() => flow.uninstall(detail.id));
                },
              },
            ] satisfies ModalAction[]
          }
        >
          <p className="mp-modal__text">
            Uninstall <strong>{detail.name}</strong>? You can reinstall it later from the
            Available tab.
          </p>
        </Modal>
      )}

      {reconsent && reconsent.length > 0 && (
        <div className="mp-detail__reconsent" role="alert">
          <p>This update needs new permissions before it can run:</p>
          <ul role="list">
            {reconsent.map((p) => (
              <li key={p.capability}>
                <strong>{CAPABILITY_LABELS[p.capability]}</strong>: {p.reason}
              </li>
            ))}
          </ul>
          <div className="mp-detail__reconsent-actions">
            <button
              type="button"
              className="mp-btn mp-btn--primary"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await flow.setEnabled(detail.id, true);
                  setReconsent(null);
                })
              }
            >
              Approve and enable
            </button>
            <button
              type="button"
              className="mp-btn"
              disabled={busy}
              onClick={() => setReconsent(null)}
            >
              Keep disabled
            </button>
          </div>
        </div>
      )}

      {error && <p className="mp-detail__error" role="alert">{error}</p>}

      {(detail.manifest.contributes?.commands?.length ?? 0) > 0 && (
        <section className="mp-commands" aria-label="Commands">
          <h4 className="mp-commands__title">Commands</h4>
          <ul className="mp-commands__list" role="list">
            {detail.manifest.contributes?.commands?.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="mp-commands__row"
                  onClick={() => runInPalette(c.id)}
                  title={`Open ${commandSlash(detail.manifest, c.id)} in the command palette`}
                >
                  <code className="mp-commands__slash">{commandSlash(detail.manifest, c.id)}</code>
                  <span className="mp-commands__label">{c.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mp-detail__readme">
        <PluginReadme markdown={detail.readme} resolveAsset={detail.resolveAsset} onOpenLink={onOpenLink} />
      </div>
    </article>
  );
}
